import React, { createContext, useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

const AppContext = createContext();
const DEFAULT_TTL = 24 * 60 * 60 * 1000;
const TERMINAL_SUBSCRIPTION_STATUSES = new Set(['cancel', 'canceled', 'cancelled', 'expired']);
const BILLING_CHECK_TIMEOUT_MS = 5000;
const IMPERSONATION_STORAGE_KEY = 'impersonationSession';
const EMPTY_ROLE = { id: null, type: null };

const TARGET_TYPE_TO_ROLE = {
  venue: 'venue',
  adv: 'adv',
  tsvenue: 'tsVenue',
  ts_venue: 'tsVenue',
  tsadv: 'tsAdv',
  ts_adv: 'tsAdv',
  tscontent: 'tsContent',
  ts_content: 'tsContent',
};

const setWithExpiry = (key, value, ttl = DEFAULT_TTL) => {
  const now = Date.now();
  const item = { value, expiry: now + ttl, ttl };
  localStorage.setItem(key, JSON.stringify(item));
};

const getWithExpiry = (key) => {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;
  try {
    const item = JSON.parse(itemStr);
    const now = Date.now();
    if (now > item.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    localStorage.setItem(key, JSON.stringify({ ...item, expiry: now + (item.ttl || DEFAULT_TTL) }));
    return item.value;
  } catch {
    localStorage.removeItem(key);
    return null;
  }
};

const normalizeTargetRoleType = (value) => {
  if (typeof value !== 'string') return null;
  return TARGET_TYPE_TO_ROLE[value.trim().toLowerCase()] || null;
};

const getTargetRoleFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  const targetId = params.get('target_id');
  const targetType = normalizeTargetRoleType(params.get('target_type'));

  if (!targetId || !targetType) {
    return null;
  }

  return { id: targetId, type: targetType };
};

const removeTargetParamsFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  if (!params.get('target_id') && !params.get('target_type')) {
    return;
  }

  params.delete('target_id');
  params.delete('target_type');
  window.history.replaceState(
    null,
    '',
    window.location.pathname + (params.toString() ? `?${params.toString()}` : '')
  );
};

const getStoredImpersonationSession = () => {
  const stored = getWithExpiry(IMPERSONATION_STORAGE_KEY);
  const targetRole = stored?.targetRole;
  const normalizedType = normalizeTargetRoleType(targetRole?.type);

  if (!stored?.isImpersonatedSession || !targetRole?.id || !normalizedType) {
    return null;
  }

  return {
    isImpersonatedSession: true,
    targetRole: {
      id: targetRole.id,
      type: normalizedType,
    },
  };
};

const normalizeScopedTargetFromServer = (scopedTarget) => {
  const targetId = scopedTarget?.targetId || scopedTarget?.id;
  const targetType = normalizeTargetRoleType(scopedTarget?.targetType || scopedTarget?.type);

  if (!targetId || !targetType) {
    return null;
  }

  return { id: targetId, type: targetType };
};

const buildMatchedRole = (data, targetRole) => {
  if (!targetRole?.id || !targetRole?.type) {
    return null;
  }

  if (!data?.roleInfoIdCollection?.[targetRole.type]?.includes(targetRole.id)) {
    return null;
  }

  const matchedRole = data?.roleInfo?.[targetRole.type]?.find((item) => item.id === targetRole.id);
  return {
    id: targetRole.id,
    type: targetRole.type,
    permissionLevel: matchedRole?.permissionLevel,
  };
};

export const AppProvider = ({ children }) => {
  const navigate = useNavigate();
  const initialParams = new URLSearchParams(window.location.search);
  const initialUrlToken = initialParams.get('token');
  const initialTargetRole = getTargetRoleFromUrl();
  const storedImpersonation = getStoredImpersonationSession();
  const initialLockedRole = initialTargetRole || storedImpersonation?.targetRole || null;

  const [token, setTokenState] = useState(() => {
    if (initialUrlToken) {
      initialParams.delete('token');
      window.history.replaceState(
        null,
        '',
        window.location.pathname + (initialParams.toString() ? `?${initialParams.toString()}` : '')
      );
      setWithExpiry('token', initialUrlToken);
      return initialUrlToken;
    }
    return getWithExpiry('token');
  });
  const [userInfo, setUserInfo] = useState(null);
  const [isMasterUser, setIsMasterUserState] = useState(() => getWithExpiry('isMasterUser') === 'true');
  const [selectedRole, setSelectedRoleState] = useState(() => {
    if (initialLockedRole) {
      return initialLockedRole;
    }
    if (initialUrlToken) {
      return EMPTY_ROLE;
    }
    const saved = sessionStorage.getItem('selectedRole');
    return saved ? JSON.parse(saved) : EMPTY_ROLE;
  });
  const [impersonationState, setImpersonationState] = useState(() => (
    initialLockedRole
      ? { isImpersonatedSession: true, targetRole: initialLockedRole }
      : { isImpersonatedSession: false, targetRole: null }
  ));
  const [impersonationSessionInvalid, setImpersonationSessionInvalid] = useState(false);
  const [subscriptionGate, setSubscriptionGate] = useState({
    isBlocked: false,
    billingOnly: false,
    status: null,
    checkedRoleId: null,
    reason: '',
  });
  const [subscriptionGateLoading, setSubscriptionGateLoading] = useState(false);
  const [loading, setLoading] = useState(() => Boolean(token));
  const [authBootstrapError, setAuthBootstrapError] = useState('');
  // Single fetch of the backend's canonical imported-events venue config
  // (which venues support the feature, and whether each uses an automatic
  // scraper vs manual local-JSON sync) - shared here so SideNavigationBar
  // and the Imported Events page both read the same data instead of each
  // keeping their own hardcoded venue list, which is exactly how the
  // Yarrawonga sidebar-entry gap happened (one copy got updated, another
  // didn't).
  const [importedEventSupportedVenues, setImportedEventSupportedVenues] = useState([]);
  // Distinguishes "haven't checked yet" from "checked, and it's really
  // empty/unsupported" - without this, a page that gates its content on
  // isSupportedVenue would briefly render its "not supported" state for
  // every venue on first load, before this fetch has had a chance to
  // resolve.
  const [importedEventSupportedVenuesLoaded, setImportedEventSupportedVenuesLoaded] = useState(false);
  const roleInitialised = useRef(Boolean(initialLockedRole || (!initialUrlToken && sessionStorage.getItem('selectedRole'))));

  const lockImpersonationSession = useCallback((targetRole) => {
    if (!targetRole?.id || !targetRole?.type) return;
    const nextState = { isImpersonatedSession: true, targetRole };
    setImpersonationState(nextState);
    setWithExpiry(IMPERSONATION_STORAGE_KEY, nextState);
  }, []);

  const clearImpersonationSession = useCallback(() => {
    setImpersonationState({ isImpersonatedSession: false, targetRole: null });
    setImpersonationSessionInvalid(false);
    localStorage.removeItem(IMPERSONATION_STORAGE_KEY);
  }, []);

  const logout = useCallback(() => {
    setTokenState(null);
    setUserInfo(null);
    setIsMasterUserState(false);
    setSelectedRoleState(EMPTY_ROLE);
    clearImpersonationSession();
    localStorage.removeItem('token');
    localStorage.removeItem('isMasterUser');
    sessionStorage.clear();
    navigate('/login');
  }, [clearImpersonationSession, navigate]);

  const fetchUserInfo = useCallback(
    async (nextToken) => {
      // 鍙湁鐪熸鐨勭櫥褰曞け鏁堬紙401/403锛夋墠搴旇瑙﹀彂鍏ㄥ眬鐧诲嚭锛歭ogout() 浼氭竻鎺?localStorage 閲?
      // 鎵€鏈夋爣绛鹃〉鍏变韩鐨?token锛屼互鍙婅繖涓爣绛鹃〉鑷繁鐨?selectedRole锛堣鐫€褰撳墠鏄摢涓?venue锛夈€?
      // 涔嬪墠杩欓噷浠讳綍澶辫触鈥斺€斿寘鎷綉缁滄姈鍔ㄣ€佸悗绔伓鍙?5xx銆佽姹傝秴鏃垛€斺€旈兘鏃犳潯浠?logout()锛?
      // 瀵艰嚧涓€涓┖闂插緢涔呫€佹伆濂借繖娆¤姹傚け璐ョ殑鏍囩椤典細鎶婃墍鏈夋爣绛鹃〉閮界櫥鍑猴紱閲嶆柊鐧诲綍鍚?
      // selectedRole 涓簡锛屼細閫€鍥炲埌"鍒楄〃绗竴涓?venue"锛岃〃鐜颁负杩欎釜鏍囩椤电獊鐒跺彉鎴愪簡
      // 鍒殑锛堥€氬父鏄渶杩戝湪鍏跺畠鏍囩椤佃闂繃鐨勶級venue銆?
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/user/info`, {
        headers: { Authorization: `Bearer ${nextToken}` },
      });

      if (res.status === 401 || res.status === 403) {
        const authError = new Error('Session expired');
        authError.isAuthError = true;
        throw authError;
      }
      if (!res.ok) {
        throw new Error(`Failed to fetch user info: HTTP ${res.status}`);
      }

      const data = await res.json();
      // Master (permissionLevel 9) accounts are blocked from logging into this app
      // directly with their own credentials - that's the "wrong door" case this
      // check exists for. It should NOT fire for an impersonated session (staff
      // deliberately used "Go to Advanced Editor"), even if the venue/adv/etc.
      // they're impersonating happens to be owned by a master-level account -
      // sessionScope is only set on impersonated tokens, never on a direct login.
      if (data && data.user && data.user.permissionLevel === 9 && !data.sessionScope) {
        alert('Master user cannot access this page, use Master page instead.');
        const masterUserError = new Error('Master user not allowed');
        masterUserError.isAuthError = true;
        throw masterUserError;
      }
      setUserInfo(data);
      return data;
    },
    []
  );

  const loadUserSession = useCallback(async (nextToken) => {
    if (!nextToken) {
      setLoading(false);
      setAuthBootstrapError('');
      return;
    }

    setLoading(true);

    try {
      const data = await fetchUserInfo(nextToken);

      setUserInfo(data);
      setAuthBootstrapError('');
      const { roleInfoIdCollection } = data;
      const urlTargetRole = getTargetRoleFromUrl();
      const serverScopedTarget = normalizeScopedTargetFromServer(data?.sessionScope);

      if (urlTargetRole) {
        removeTargetParamsFromUrl();
      }

      if (serverScopedTarget) {
        lockImpersonationSession(serverScopedTarget);
        const matchedRole = buildMatchedRole(data, serverScopedTarget);

        if (matchedRole) {
          setSelectedRoleState(matchedRole);
          setImpersonationSessionInvalid(false);
        } else {
          setSelectedRoleState(serverScopedTarget);
          setImpersonationSessionInvalid(true);
        }

        roleInitialised.current = true;
        return;
      }

      clearImpersonationSession();
      setImpersonationSessionInvalid(false);

      if (!roleInitialised.current) {
        if (roleInfoIdCollection.venue?.length) {
          setSelectedRoleState({ id: roleInfoIdCollection.venue[0], type: 'venue' });
        } else if (roleInfoIdCollection.adv?.length) {
          setSelectedRoleState({ id: roleInfoIdCollection.adv[0], type: 'adv' });
        } else if (roleInfoIdCollection.tsVenue?.length) {
          setSelectedRoleState({ id: roleInfoIdCollection.tsVenue[0], type: 'tsVenue' });
        } else if (roleInfoIdCollection.tsAdv?.length) {
          setSelectedRoleState({ id: roleInfoIdCollection.tsAdv[0], type: 'tsAdv' });
        } else if (roleInfoIdCollection.tsContent?.length) {
          setSelectedRoleState({ id: roleInfoIdCollection.tsContent[0], type: 'tsContent' });
        }
        roleInitialised.current = true;
      }
    } catch (error) {
      if (error?.isAuthError) {
        logout();
        return;
      }

      console.error('Failed to load user info:', error);
      setAuthBootstrapError(error?.message || 'Unable to refresh session data right now. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [clearImpersonationSession, fetchUserInfo, lockImpersonationSession, logout]);

  const retryFetchUserInfo = useCallback(async () => {
    if (!token) return;
    await loadUserSession(token);
  }, [loadUserSession, token]);

  const setSelectedRole = useCallback((nextRoleOrUpdater) => {
    setSelectedRoleState((currentRole) => {
      const resolvedRole = typeof nextRoleOrUpdater === 'function'
        ? nextRoleOrUpdater(currentRole)
        : nextRoleOrUpdater;

      if (impersonationState.isImpersonatedSession && impersonationState.targetRole) {
        return {
          ...impersonationState.targetRole,
          permissionLevel: resolvedRole?.permissionLevel ?? currentRole?.permissionLevel,
        };
      }

      return resolvedRole || EMPTY_ROLE;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    impersonationState.isImpersonatedSession,
    impersonationState.targetRole?.id,
    impersonationState.targetRole?.type,
  ]);

  useEffect(() => {
    if (!impersonationState.isImpersonatedSession || !impersonationState.targetRole) {
      return;
    }

    if (
      selectedRole?.id !== impersonationState.targetRole.id ||
      selectedRole?.type !== impersonationState.targetRole.type
    ) {
      setSelectedRoleState((currentRole) => ({
        ...impersonationState.targetRole,
        permissionLevel: currentRole?.permissionLevel,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    impersonationState.isImpersonatedSession,
    impersonationState.targetRole?.id,
    impersonationState.targetRole?.type,
    selectedRole?.id,
    selectedRole?.type,
  ]);

  const setToken = useCallback(
    (newToken) => {
      if (newToken) {
        // Every fresh login must start with its own venue, not whatever
        // selectedRole is left over from the previous session in this tab -
        // roleInitialised is only ever set to true, never reset, so without
        // this it stays latched from the first login of the tab's lifetime
        // and every subsequent account (logged in without a full page
        // reload) never gets a chance to have its own venue selected.
        roleInitialised.current = false;
        sessionStorage.removeItem('selectedRole');
        setSelectedRoleState(EMPTY_ROLE);
        setLoading(true);
      }
      setWithExpiry('token', newToken);
      setTokenState(newToken);
    },
    []
  );
  const setIsMasterUser = useCallback(
    (flag) => setWithExpiry('isMasterUser', flag ? 'true' : 'false') || setIsMasterUserState(flag),
    []
  );

  useEffect(() => {
    if (window.location.pathname === '/') {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get('token');
      if (urlToken) {
        setToken(urlToken);

        params.delete('token');
        const newSearch = params.toString();
        const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
        navigate(newUrl, { replace: true });
      }
    }

    if (!token) {
      setLoading(false);
      setAuthBootstrapError('');
      return;
    }

    loadUserSession(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loadUserSession, navigate, setToken]);
  useEffect(() => {
    sessionStorage.setItem('selectedRole', JSON.stringify(selectedRole));
  }, [selectedRole]);

  // Narrowed to just the venue role list so the subscription-check effect
  // below doesn't re-run every time the (much larger) userInfo object is
  // replaced with a new-but-equivalent reference.
  const venueRoleInfo = userInfo?.roleInfo?.venue;

  useEffect(() => {
    const roleType = selectedRole?.type;
    const roleId = selectedRole?.id;

    if (!token || !roleId || roleType !== 'venue') {
      setSubscriptionGate({
        isBlocked: false,
        billingOnly: false,
        status: null,
        checkedRoleId: roleId || null,
        reason: '',
      });
      setSubscriptionGateLoading(false);
      return;
    }

    const venueRoles = venueRoleInfo || [];
    const currentVenue = venueRoles.find((item) => item.id === roleId);
    const hasStripeCustomerId = Boolean(currentVenue?.stripeCustomerId || currentVenue?.stripeCutsomerId);

    if (!hasStripeCustomerId) {
      setSubscriptionGate({
        isBlocked: false,
        billingOnly: false,
        status: null,
        checkedRoleId: roleId,
        reason: '',
      });
      setSubscriptionGateLoading(false);
      return;
    }

    let ignore = false;
    setSubscriptionGateLoading(true);

    (async () => {
      try {
        const resp = await fetch(
          `${process.env.REACT_APP_BILLING_API_KEY}/api/private/billing/check-subscription`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ venueId: roleId }),
            signal: AbortSignal.timeout(BILLING_CHECK_TIMEOUT_MS),
          }
        );

        if (!resp.ok) {
          throw new Error('Failed to check subscription access');
        }

        const data = await resp.json().catch(() => ({}));
        const billingData = data?.data || data;
        const subscription = billingData?.subscription || null;
        const status = String(subscription?.status || '').toLowerCase();
        const validToMs = subscription?.validTo ? new Date(subscription.validTo).getTime() : NaN;
        const isWithinPaidPeriod = Number.isFinite(validToMs) && validToMs > Date.now();
        const isBlocked = TERMINAL_SUBSCRIPTION_STATUSES.has(status) && !isWithinPaidPeriod;

        if (ignore) return;
        setSubscriptionGate({
          isBlocked,
          billingOnly: isBlocked,
          status: status || null,
          checkedRoleId: roleId,
          reason: isBlocked ? 'subscription_inactive' : '',
        });
      } catch {
        if (ignore) return;
        setSubscriptionGate({
          isBlocked: false,
          billingOnly: false,
          status: null,
          checkedRoleId: roleId,
          reason: 'subscription_check_failed',
        });
      } finally {
        if (!ignore) {
          setSubscriptionGateLoading(false);
        }
      }
    })();

    return () => {
      ignore = true;
    };
  }, [selectedRole?.id, selectedRole?.type, token, venueRoleInfo]);

  useEffect(() => {
    setWithExpiry('isMasterUser', isMasterUser ? 'true' : 'false');
  }, [isMasterUser]);

  useEffect(() => {
    if (!token) {
      setImportedEventSupportedVenues([]);
      setImportedEventSupportedVenuesLoaded(false);
      return undefined;
    }

    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ts/imported-events/supported-venues`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!ignore) setImportedEventSupportedVenues(Array.isArray(data.items) ? data.items : []);
      } catch {
        // Non-critical - anything reading this just falls back to an empty
        // list (no imported-events nav entry/venue options shown) rather
        // than blocking login or any other part of the app.
      } finally {
        // Runs whether the fetch succeeded, failed, or was skipped (!res.ok) -
        // callers should stop treating this as "still loading" either way,
        // rather than waiting forever on a fetch that already gave up.
        if (!ignore) setImportedEventSupportedVenuesLoaded(true);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [token]);

  const uploadImage = useCallback(async (image, width, height) => {
    try {
      const MAX_FILE_SIZE = 5 * 1024 * 1024;
      if (image.size > MAX_FILE_SIZE) {
        alert('File size exceeds 5MB');
        return;
      }

      const formData = new FormData();
      formData.append('image', image);
      formData.append('width', width);
      formData.append('height', height);

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/private/image/upload`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        return data.fileUrl;
      }

      console.error('Failed to fetch user info');
      navigate('/login');
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  }, [token, navigate]);

  const contextValue = useMemo(
    () => ({
      token,
      setToken,
      userInfo,
      isMasterUser,
      setIsMasterUser,
      selectedRole,
      setSelectedRole,
      isImpersonatedSession: impersonationState.isImpersonatedSession,
      impersonationTargetRole: impersonationState.targetRole,
      impersonationSessionInvalid,
      subscriptionGate,
      subscriptionGateLoading,
      logout,
      uploadImage,
      loading,
      authBootstrapError,
      retryFetchUserInfo,
      importedEventSupportedVenues,
      importedEventSupportedVenuesLoaded,
    }),
    [
      token,
      setToken,
      userInfo,
      isMasterUser,
      setIsMasterUser,
      selectedRole,
      setSelectedRole,
      impersonationState.isImpersonatedSession,
      impersonationState.targetRole,
      impersonationSessionInvalid,
      subscriptionGate,
      subscriptionGateLoading,
      logout,
      uploadImage,
      loading,
      authBootstrapError,
      retryFetchUserInfo,
      importedEventSupportedVenues,
      importedEventSupportedVenuesLoaded,
    ]
  );

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export function useAuth() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useAuth must be used within AppProvider');
  return ctx;
}

export default AppContext;

